(function () {
  "use strict";

  var AUTO_LOAD_LIMIT = 2;
  var OBSERVER_MARGIN = "600px 0px";

  function enhanceCollection(root) {
    if (!root || root.dataset.dulongCollectionEnhanced === "true") return;
    root.dataset.dulongCollectionEnhanced = "true";

    var listElement = null;
    var paginationElement = null;
    var nextLink = null;
    var observer = null;
    var loading = false;
    var automaticLoads = 0;
    var lastScrollY = window.scrollY;
    var toolbarTicking = false;
    var lockedScrollY = 0;
    var pageScrollLocked = false;
    var filterContainer = root.querySelector(".filter-container");
    var toolbar = root.querySelector(
      ".collection-utility-bar:not(.utility-bar--sticky-mobile-copy)"
    );
    var sortButton = toolbar
      ? toolbar.querySelector('.link-dropdown__button[aria-controls="sort-dropdown-options"]')
      : null;

    function filterDrawerIsOpen() {
      return Boolean(
        filterContainer &&
          (filterContainer.classList.contains(
            "filter-container--show-filters-desktop"
          ) ||
            filterContainer.classList.contains(
              "filter-container--show-filters-mobile"
            ))
      );
    }

    function sortDrawerIsOpen() {
      return Boolean(
        sortButton && sortButton.getAttribute("aria-expanded") === "true"
      );
    }

    function collectionDrawerIsOpen() {
      return filterDrawerIsOpen() || sortDrawerIsOpen();
    }

    function lockPageScroll() {
      if (pageScrollLocked) return;

      lockedScrollY = window.scrollY;
      document.documentElement.style.setProperty(
        "--dulong-filter-scroll-offset",
        "-" + lockedScrollY + "px"
      );
      document.documentElement.classList.add("dulong-collection-drawer-open");
      pageScrollLocked = true;
    }

    function unlockPageScroll() {
      if (!pageScrollLocked) return;

      document.documentElement.classList.remove("dulong-collection-drawer-open");
      document.documentElement.style.removeProperty(
        "--dulong-filter-scroll-offset"
      );
      pageScrollLocked = false;
      window.scrollTo(0, lockedScrollY);
      lastScrollY = lockedScrollY;
    }

    function syncCollectionDrawerState() {
      root.classList.toggle("collection-sort-drawer-open", sortDrawerIsOpen());

      if (collectionDrawerIsOpen()) {
        lockPageScroll();
        root.classList.remove("collection-toolbar--hidden");
      } else {
        unlockPageScroll();
      }
    }

    function collectionControlsAreOpen() {
      return collectionDrawerIsOpen();
    }

    function updateToolbarVisibility() {
      toolbarTicking = false;
      if (!toolbar) return;

      var currentScrollY = Math.max(window.scrollY, 0);
      var movement = currentScrollY - lastScrollY;
      var toolbarStart = toolbar.offsetTop;

      if (
        currentScrollY <= toolbarStart + toolbar.offsetHeight ||
        collectionControlsAreOpen()
      ) {
        root.classList.remove("collection-toolbar--hidden");
      } else if (movement > 6) {
        root.classList.add("collection-toolbar--hidden");
      } else if (movement < -6) {
        root.classList.remove("collection-toolbar--hidden");
      }

      if (Math.abs(movement) > 6) lastScrollY = currentScrollY;
    }

    function handleToolbarScroll() {
      if (toolbarTicking) return;
      toolbarTicking = true;
      window.requestAnimationFrame(updateToolbarVisibility);
    }

    function disconnectObserver() {
      if (observer) observer.disconnect();
      observer = null;
    }

    function updateUrl(url) {
      try {
        var state = Object.assign({}, window.history.state || {}, {
          dulongCollectionUrl: url,
          dulongCollectionScrollY: window.scrollY,
        });
        window.history.replaceState(state, "", url);
      } catch (error) {
        // Browsing still works if history updates are unavailable.
      }
    }

    function refreshThemeFeatures() {
      if (window.theme && theme.ProductBlockManager) {
        theme.ProductBlockManager.loadImages(root);
      }
      if (window.theme && typeof theme.initAnimateOnScroll === "function") {
        theme.initAnimateOnScroll();
      }
    }

    function bindCurrentPagination(resetAutomaticLoads) {
      var currentList = root.querySelector(".product-list");
      var currentPagination = root.querySelector("#AjaxinatePagination");

      if (resetAutomaticLoads || (listElement && currentList !== listElement)) {
        automaticLoads = 0;
      }

      listElement = currentList;
      paginationElement = currentPagination;
      nextLink = paginationElement
        ? paginationElement.querySelector(".get-more-products")
        : null;

      disconnectObserver();
      if (!listElement || !paginationElement || !nextLink) return;

      nextLink.addEventListener("click", handleManualLoad);

      if (automaticLoads < AUTO_LOAD_LIMIT && "IntersectionObserver" in window) {
        observer = new IntersectionObserver(
          function (entries) {
            if (!entries[0].isIntersecting || loading) return;
            automaticLoads += 1;
            disconnectObserver();
            loadNextPage(nextLink.href);
          },
          { rootMargin: OBSERVER_MARGIN }
        );
        observer.observe(paginationElement);
      }
    }

    function handleManualLoad(event) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      loadNextPage(event.currentTarget.href);
    }

    function loadNextPage(url) {
      if (loading || !url || !listElement || !paginationElement) return;

      loading = true;
      var loadingList = listElement;
      var link = nextLink;
      var originalText = link ? link.textContent : "";

      if (link) {
        link.setAttribute("aria-busy", "true");
        link.textContent = link.dataset.loadingText || originalText;
      }

      fetch(url, { credentials: "same-origin" })
        .then(function (response) {
          if (!response.ok) throw new Error("Collection page could not be loaded");
          return response.text();
        })
        .then(function (html) {
          if (root.querySelector(".product-list") !== loadingList) return;

          var documentFragment = new DOMParser().parseFromString(html, "text/html");
          var newList = documentFragment.querySelector(".dulong-collection .product-list");
          var newPagination = documentFragment.querySelector(
            ".dulong-collection #AjaxinatePagination"
          );

          if (!newList || !newPagination) {
            throw new Error("Collection response was incomplete");
          }

          loadingList.insertAdjacentHTML("beforeend", newList.innerHTML);
          paginationElement.innerHTML = newPagination.innerHTML;
          updateUrl(url);
          refreshThemeFeatures();
        })
        .catch(function () {
          if (link) {
            link.removeAttribute("aria-busy");
            link.textContent = originalText;
          }
        })
        .finally(function () {
          loading = false;
          bindCurrentPagination(false);
        });
    }

    root.addEventListener("click", function (event) {
      var closeButton = event.target.closest("[data-close-filters]");
      if (!closeButton) return;

      event.preventDefault();
      if (filterContainer) {
        filterContainer.classList.remove(
          "filter-container--show-filters-desktop",
          "filter-container--show-filters-mobile"
        );
      }
      root.querySelectorAll(".toggle-btn[data-toggle-filters]").forEach(function (button) {
        button.classList.remove("toggle-btn--revealed-desktop");
      });
      var filterToggle = root.querySelector("[data-toggle-filters]");
      if (filterToggle) filterToggle.focus();
      syncCollectionDrawerState();
    });

    root.addEventListener("click", function (event) {
      var closeButton = event.target.closest("[data-close-sort]");
      if (!closeButton || !sortButton) return;

      event.preventDefault();
      event.stopPropagation();
      sortButton.setAttribute("aria-expanded", "false");
      sortButton.focus();
      syncCollectionDrawerState();
    });

    root.addEventListener("click", function (event) {
      if (
        event.target.closest("[data-toggle-filters]") ||
        event.target.closest(".link-dropdown__button")
      ) {
        root.classList.remove("collection-toolbar--hidden");
      }
    });

    window.addEventListener("scroll", handleToolbarScroll, { passive: true });

    if (filterContainer) {
      var filterStateObserver = new MutationObserver(syncCollectionDrawerState);
      filterStateObserver.observe(filterContainer, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    if (sortButton) {
      var sortStateObserver = new MutationObserver(syncCollectionDrawerState);
      sortStateObserver.observe(sortButton, {
        attributes: true,
        attributeFilter: ["aria-expanded"],
      });
    }

    var mutationObserver = new MutationObserver(function () {
      var currentList = root.querySelector(".product-list");
      var currentPagination = root.querySelector("#AjaxinatePagination");
      if (currentList !== listElement || currentPagination !== paginationElement) {
        bindCurrentPagination(true);
      }
    });

    mutationObserver.observe(root, { childList: true, subtree: true });
    bindCurrentPagination(true);
    syncCollectionDrawerState();
    updateToolbarVisibility();
  }

  function init(scope) {
    var context = scope || document;
    if (context.matches && context.matches('[data-section-type="collection-template"]')) {
      enhanceCollection(context);
    }
    context
      .querySelectorAll('[data-section-type="collection-template"]')
      .forEach(enhanceCollection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    init(event.target);
  });
})();
